import type { Vec3 } from '../../geo/types';

/** A set course as the painter view draws it: its head from yardarm to yardarm, and its drop. */
export interface DrawnCourse {
  readonly head: readonly [Vec3, Vec3];
  readonly drop: number;
}

/**
 * How far the painter view turns a set course's normals, as the tangent of the turn: across the
 * course towards its middle, full at the yardarms, and down, full at the foot. The painter gives
 * each course a lit side and a side in half shade across its belly, and a darker lower belly: of
 * his courses' cloth 31-46% lies lit at L 54-60 and 32-42% in half shade at L 40-49 (R8, R20, R26
 * traced on the scan), where ours drawn flat lay 48-54% at L 58 and 21-26% at L 53. With the
 * light from the painter's left, a course drawn concave to him in this way is shaded on its left
 * side and lit on its right, as his are.
 */
export const BELLY = { across: 1.2, foot: 1.0 } as const;

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high);

/**
 * The normal the painter view shades a set course with at `at`, a point of it as drawn, from the
 * `normal` of the flat cloth drawn there: either face turned in towards the middle across the
 * course and down towards its foot, by BELLY.
 */
export function belliedNormal(course: DrawnCourse, at: Vec3, normal: Vec3): Vec3 {
  const [a, b] = course.head;
  const span = Math.hypot(b[0] - a[0], b[2] - a[2]);
  const axis: Vec3 = [(b[0] - a[0]) / span, 0, (b[2] - a[2]) / span];
  const middle: Vec3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
  const across = clamp(
    (2 * ((at[0] - middle[0]) * axis[0] + (at[2] - middle[2]) * axis[2])) / span,
    -1,
    1
  );
  const down = clamp((middle[1] - at[1]) / course.drop, 0, 1);
  const turned: Vec3 = [
    normal[0] - BELLY.across * across * axis[0],
    normal[1] - BELLY.foot * down,
    normal[2] - BELLY.across * across * axis[2],
  ];
  const length = Math.hypot(...turned);
  return [turned[0] / length, turned[1] / length, turned[2] / length];
}
